import RestoreIcon from "@mui/icons-material/Restore";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import { Box } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../components/UIToolkit/Animations/JumpingDots";
import EmployeeStatusChip from "../../../../../components/UIToolkit/Badges/EmployeeStatusChip";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import type { MultiSortOption } from "../../../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { useDebouncedSave } from "../../../../../hooks/useDebouncedSave";
import { hrisMappingEmployeeStatusSortingKey } from "../../../../../services/LocalStorageService";
import { EffectiveEmployeeStatus, type EmployeeStatusMapping } from "../../../../../utils/models";
import { EmployeeStatusChangeConfirmDialog } from "./EmployeeStatusChangeConfirmDialog";
import { useDeleteEmployeeStatusMapping, useDepartmentEmployeeStatusMapping, useUpsertEmployeeStatusMapping } from "./hrisMappingHooks";
import { HoverActionCell } from "./utils";

/**
 * Translation key per effective status; all four are selectable — the backend accepts any of them.
 * A `null` status (raw string not yet mapped) has no entry here and is rendered as a distinct
 * "Unmapped" chip instead, so every lookup into this map must first narrow out `null`.
 */
const STATUS_LABEL_KEY: Record<EffectiveEmployeeStatus, string> = {
	[EffectiveEmployeeStatus.Active]: "employee.status.chip.active",
	[EffectiveEmployeeStatus.Inactive]: "employee.status.chip.inactive",
	[EffectiveEmployeeStatus.OnLeave]: "employee.status.chip.on_leave",
	[EffectiveEmployeeStatus.Suspended]: "employee.status.chip.suspended",
};

export const EmployeeStatusMappingGrid: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const { data, isLoading, isError } = useDepartmentEmployeeStatusMapping(departmentId);
	const upsertMutation = useUpsertEmployeeStatusMapping(departmentId);
	const deleteMutation = useDeleteEmployeeStatusMapping(departmentId);
	const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
	const [pendingChange, setPendingChange] = useState<{ rawString: string; status: EffectiveEmployeeStatus } | null>(null);

	// DEFAULT: useDepartmentEmployeeStatusMapping (useQuery) returns undefined until the first fetch resolves; the grid needs a stable array.
	const rows = useMemo(() => data ?? [], [data]);

	/** Precomputed status → translated label so the sort comparator does no `t()` lookups per compare. */
	const statusLabelByValue = useMemo(() => {
		const map = {} as Record<EffectiveEmployeeStatus, string>;
		for (const value of Object.values(EffectiveEmployeeStatus)) map[value] = t(STATUS_LABEL_KEY[value]);
		return map;
	}, [t]);

	/**
	 * Null-safe status comparator: Unmapped (null) rows cluster FIRST in ascending order so the
	 * "needs attention" rows surface at the top; concrete statuses fall back to their translated label.
	 */
	const compareStatus = useCallback(
		(a: EmployeeStatusMapping, b: EmployeeStatusMapping): 1 | 0 | -1 => {
			if (a.status === b.status) return 0;
			if (a.status === null) return -1;
			if (b.status === null) return 1;
			const cmp = statusLabelByValue[a.status].localeCompare(statusLabelByValue[b.status]);
			return cmp > 0 ? 1 : cmp < 0 ? -1 : 0;
		},
		[statusLabelByValue],
	);

	const statusSortOptions: MultiSortOption<EmployeeStatusMapping>[] = useMemo(
		() => [
			{ label: t("hris.mapping.column.rawString"), sortColumns: ["rawString"] },
			{ label: t("hris.mapping.column.status"), sortColumns: ["status"], customSortFunction: compareStatus },
			{ label: t("hris.mapping.column.isOverride"), sortColumns: ["isOverride"] },
		],
		[t, compareStatus],
	);

	const statusValueOptions = useMemo(
		() => Object.values(EffectiveEmployeeStatus).map((value) => ({ value, label: statusLabelByValue[value] })),
		[statusLabelByValue],
	);

	const scheduleSave = useDebouncedSave<{ rawString: string; status: string }>((payload) => {
		track("employee_status_mapping_save_start", { department_id: departmentId, raw_string: payload.rawString, status: payload.status });
		upsertMutation.mutate(payload, {
			onSuccess: () => track("employee_status_mapping_save_success", { department_id: departmentId, raw_string: payload.rawString, status: payload.status }),
			onError: (err) => {
				trackError("employee_status_mapping_save_error", { department_id: departmentId, error_message: err?.message });
				showErrorSnackbar(t("hris.mapping.saveError"), {}, err);
			},
			onSettled: () =>
				setPendingKeys((prev) => {
					const next = new Set(prev);
					next.delete(payload.rawString);
					return next;
				}),
		});
	});

	const handleDelete = useCallback(
		(rawString: string) => {
			track("employee_status_mapping_delete_start", { department_id: departmentId, raw_string: rawString });
			deleteMutation.mutate(rawString, {
				onSuccess: () => track("employee_status_mapping_delete_success", { department_id: departmentId, raw_string: rawString }),
				onError: (err) => {
					trackError("employee_status_mapping_delete_error", { department_id: departmentId, error_message: err?.message });
					showErrorSnackbar(t("hris.mapping.deleteError"), {}, err);
				},
			});
		},
		[deleteMutation, departmentId, track, trackError, showErrorSnackbar, t],
	);

	const handleConfirmChange = useCallback(() => {
		if (pendingChange === null) return;
		const { rawString, status } = pendingChange;
		setPendingKeys((prev) => new Set(prev).add(rawString));
		scheduleSave(rawString, { rawString, status });
		setPendingChange(null);
	}, [pendingChange, scheduleSave]);

	/**
	 * The edit flow only ever goes null → a concrete value; null is never persisted. The non-null
	 * guard also narrows the type so `pendingChange` never holds null. The change is staged and the
	 * write deferred until the admin confirms — a status change can (de)activate matching employees —
	 * so we return `oldRow` here to keep the current mapping visible until the write actually happens.
	 */
	const processRowUpdate = useCallback((newRow: EmployeeStatusMapping, oldRow: EmployeeStatusMapping): EmployeeStatusMapping => {
		if (newRow.status !== null && newRow.status !== oldRow.status) {
			setPendingChange({ rawString: newRow.rawString, status: newRow.status });
			return oldRow;
		}
		return newRow;
	}, []);

	const columns: GridColDef<EmployeeStatusMapping>[] = useMemo(
		() => [
			{
				field: "rawString",
				headerName: t("hris.mapping.column.rawString"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "status",
				headerName: t("hris.mapping.column.status"),
				flex: 1,
				minWidth: 160,
				editable: true,
				type: "singleSelect",
				valueOptions: statusValueOptions,
				display: "flex",
				renderCell: (params) =>
					params.row.status === null ? (
						<EmployeeStatusChip status="Unmapped" dataTestId={`hris-mapping-employee-status-unmapped-${params.row.rawString}`} />
					) : (
						t(STATUS_LABEL_KEY[params.row.status])
					),
			},
			{
				field: "isOverride",
				headerName: t("hris.mapping.column.isOverride"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) =>
					pendingKeys.has(params.row.rawString) ? (
						<JumpingDots />
					) : (
						<HoverActionCell
							show={params.row.isOverride}
							actionIcon={<RestoreIcon fontSize="small" />}
							tooltip={params.row.defaultDisplay ? `${t("hris.mapping.resetToDefault")}: ${params.row.defaultDisplay}` : t("hris.mapping.resetToDefault")}
							onAction={() => handleDelete(params.row.rawString)}
						/>
					),
			},
		],
		[t, statusValueOptions, handleDelete, pendingKeys],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
			<SystemDataGrid<EmployeeStatusMapping>
				rows={rows}
				columns={columns}
				getRowId={(row) => row.rawString}
				searchableColumns={["rawString"]}
				searchPlaceholder={t("hris.mapping.search.placeholder.rawString")}
				sortingOptions={statusSortOptions}
				defaultSortState={[{ sortColumns: ["rawString"], sortOrder: "asc" }]}
				localStorageKey={hrisMappingEmployeeStatusSortingKey}
				processRowUpdate={processRowUpdate}
				loading={isLoading}
				error={isError}
				containedHeight
				emptyState={<EmptyState icon={<TableRowsOutlined />} title={t("hris.mapping.grid.empty.title")} hint={t("hris.mapping.grid.empty.hintStatus")} />}
				dataTestId="hris-mapping-employee-status"
			/>
			{pendingChange !== null && (
				<EmployeeStatusChangeConfirmDialog
					key={pendingChange.rawString}
					open
					rawString={pendingChange.rawString}
					newStatusLabel={statusLabelByValue[pendingChange.status]}
					onConfirm={handleConfirmChange}
					onClose={() => setPendingChange(null)}
				/>
			)}
		</Box>
	);
};
